import * as React from 'react'
import { SupportUser } from '../api'
import { LoadingState } from './base'
import { idObjectToArray } from 'utils'

export type SupportUsers = {
  [key: string]: SupportUser
}

export interface SupportUsersState {
  supportUsers: SupportUsers
  supportUsersList: SupportUser[]
  loadingState: LoadingState
}

type LoadSupportUsersAction = {
  type: 'LoadSupportUsers'
  payload: SupportUser[]
  timestamp: number | null
}

type EditSupportUserAction = {
  type: 'EditSupportUser'
  payload: SupportUser
}

type DeleteSupportUserAction = {
  type: 'DeleteSupportUser'
  payload: string
}

type ClearAction = {
  type: 'Clear'
}

export type SupportUserAction =
  | LoadSupportUsersAction
  | EditSupportUserAction
  | DeleteSupportUserAction
  | ClearAction

export interface SupportUsersContextState {
  state: SupportUsersState
  dispatch: (action: SupportUserAction) => void
}

const DEFAULT_SUPPORTUSERS_STATE: SupportUsersContextState = {
  state: {
    supportUsers: {},
    supportUsersList: [],
    loadingState: {
      status: 'None',
      lastApiReceiveAt: null,
    },
  } as SupportUsersState,
  dispatch: (action: SupportUserAction) => {},
}

const reducer = (state: SupportUsersState, action: SupportUserAction) => {
  switch (action.type) {
    case 'Clear': {
      return DEFAULT_SUPPORTUSERS_STATE.state
    }
    case 'LoadSupportUsers': {
      const supportUsers = action.payload.reduce((accum, current) => {
        accum[current.id] = {
          ...current,
          id: current.id,
        }

        return accum
      }, {} as SupportUsers)
      return {
        ...state,
        supportUsers,
        supportUsersList: idObjectToArray(supportUsers),
        loadingState: {
          status: 'None',
          lastApiReceiveAt: action.timestamp,
        },
      }
    }
    case 'EditSupportUser': {
      const supportUsers = {
        ...state.supportUsers,
        [action.payload.id]: action.payload,
      }
      return {
        ...state,
        supportUsers,
        supportUsersList: idObjectToArray(supportUsers),
      }
    }
    case 'DeleteSupportUser': {
      const supportUsers = Object.keys(state.supportUsers)
        .filter(key => key !== action.payload)
        .reduce((accum, current) => {
          return {
            ...accum,
            [current]: state.supportUsers[current],
          }
        }, {})
      return {
        ...state,
        supportUsers,
        supportUsersList: idObjectToArray(supportUsers),
      }
    }
    default:
      return state
  }
}

export const SupportUsersContext = React.createContext<
  SupportUsersContextState
>(DEFAULT_SUPPORTUSERS_STATE)

type SupportUsersContextProviderProps = {
  children: any
}

export const SupportUsersContextProvider: React.FunctionComponent<SupportUsersContextProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(
    reducer as React.Reducer<SupportUsersState, SupportUserAction>,
    DEFAULT_SUPPORTUSERS_STATE.state,
  )
  const value = { state, dispatch }

  return (
    <SupportUsersContext.Provider value={value}>
      {children}
    </SupportUsersContext.Provider>
  )
}

export const SupportUsersContextConsumer = SupportUsersContext.Consumer
